import { ChangeEvent, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  CButton, CButtonGroup, CCard, CCardBody, CCardHeader, CCol, CDataTable, CFormGroup, CInput, CLabel,
  CModal, CModalBody, CModalFooter, CModalHeader, CModalTitle, CPagination, CRow, CTooltip,
} from "@coreui/react";
import { getItem, getList } from "../../api/generics";
import {
  incidentDelivery, incidentProgress, SIFEN_INCIDENT_VIEW_PERMISSIONS, TaxpayerSifenIncident,
} from "../../models/sifen-incident";
import { RootState } from "../../store";
import { SUCCESS } from "../../utils/constants/tags";
import PermissionRequired from "../permissions/PermissionRequired";
import SelectBootstrap from "../react-select-bootstrap/SelectBootstrap";

import SifenIncidentConversationModal from "./SifenIncidentConversationModal";

const PAGE_SIZE = 10;
const fields = [
  { key: "issueLabel", _classes: ["text-center"], label: "Incidencia" },
  { key: "taxPayerName", _classes: ["text-center"], label: "Contribuyente" },
  { key: "cdc", _classes: ["text-center"], label: "Documento / CDC" },
  { key: "progressStatus", _classes: ["text-center"], label: "Seguimiento" },
  { key: "deliveryStatus", _classes: ["text-center"], label: "Correo" },
  { key: "actions", _classes: ["text-center"], label: "Acciones", filter: false },
];
const progressOptions = Object.entries(incidentProgress).map(([value, label]) => ({ value, label }));
const deliveryOptions = Object.entries(incidentDelivery).map(([value, label]) => ({ value, label }));
const date = (value?: string | null) => value ? new Date(value).toLocaleString("es-PY") : "—";

const TaxpayerIncidents = ({ taxPayerId, taxPayerName }: { taxPayerId: number; taxPayerName?: string }) => {
  const [items, setItems] = useState<TaxpayerSifenIncident[]>([]);
  const [count, setCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [progressFilter, setProgressFilter] = useState<string[]>([]);
  const [deliveryFilter, setDeliveryFilter] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [reload, setReload] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [conversationId, setConversationId] = useState<number>();
  const [selectedId, setSelectedId] = useState<number>();
  const [detail, setDetail] = useState<TaxpayerSifenIncident>();
  const [detailReload, setDetailReload] = useState(0);
  const [detailError, setDetailError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    const filters = new Map<string, string | string[]>([
      ["tax_payer_id", String(taxPayerId)], ["progress_status", progressFilter],
      ["delivery_status", deliveryFilter], ["search", search],
    ]);
    getList<TaxpayerSifenIncident>("/sifen_reports/", PAGE_SIZE, offset, filters)
      .then((result) => {
        if (!active) return;
        if (result.status !== SUCCESS || !result.data) {
          setItems([]);
          setCount(0);
          setError(result.detail || "No se pudieron cargar las incidencias.");
          return;
        }
        setItems(result.data.items);
        setCount(result.data.count);
      })
      .catch(() => { if (active) { setItems([]); setCount(0); setError("No se pudieron cargar las incidencias."); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [taxPayerId, offset, progressFilter, deliveryFilter, search, reload]);

  useEffect(() => {
    if (selectedId === undefined) return;
    let active = true;
    setDetail(undefined);
    setDetailError("");
    getItem<TaxpayerSifenIncident>(`/sifen_reports/${selectedId}/`, new Map([["tax_payer_id", String(taxPayerId)]]))
      .then((result) => {
        if (!active) return;
        if (result.status !== SUCCESS || !result.data) {
          setDetailError(result.detail || "No se pudo cargar el seguimiento.");
          return;
        }
        setDetail(result.data);
      })
      .catch(() => { if (active) setDetailError("No se pudo cargar el seguimiento."); });
    return () => { active = false; };
  }, [taxPayerId, selectedId, reload, detailReload]);

  return <>
    <CCard>
      <CCardHeader><h1 className="h4 mb-0">Incidencias SIFEN</h1></CCardHeader>
      <CCardBody>
        <p>Consulte las incidencias de <strong>{taxPayerName || "su contribuyente"}</strong>, el estado del correo y las actualizaciones de seguimiento.</p>
        <CRow>
          <CCol md={5}><CFormGroup>
            <CLabel htmlFor="my-incidence-search">CDC, documento, incidencia o ticket</CLabel>
            <CInput id="my-incidence-search" maxLength={200} value={searchInput} onChange={(event: ChangeEvent<HTMLInputElement>) => setSearchInput(event.target.value)} />
          </CFormGroup></CCol>
          <CCol md={3}><CFormGroup>
            <CLabel htmlFor="my-incidence-progress">Seguimiento</CLabel>
            <SelectBootstrap
              inputId="my-incidence-progress"
              isMulti
              options={progressOptions}
              value={progressOptions.filter(({ value }) => progressFilter.includes(value))}
              placeholder="Todos los estados"
              onChange={(options) => { setProgressFilter(options.map(({ value }) => value)); setOffset(0); }}
            />
          </CFormGroup></CCol>
          <CCol md={3}><CFormGroup>
            <CLabel htmlFor="my-incidence-delivery">Envío de correo</CLabel>
            <SelectBootstrap
              inputId="my-incidence-delivery"
              isMulti
              options={deliveryOptions}
              value={deliveryOptions.filter(({ value }) => deliveryFilter.includes(value))}
              placeholder="Todos los envíos"
              onChange={(options) => { setDeliveryFilter(options.map(({ value }) => value)); setOffset(0); }}
            />
          </CFormGroup></CCol>
        </CRow>
        <div className="mb-3 d-flex" style={{ gap: "8px" }}>
          <CButton color="primary" onClick={() => { setSearch(searchInput.trim()); setOffset(0); }}>Buscar</CButton>
          <CButton color="secondary" disabled={loading} onClick={() => setReload((value) => value + 1)}>Actualizar</CButton>
        </div>
        {error && <div className="alert alert-danger" role="alert">{error}</div>}
        {loading && <p role="status">Cargando incidencias...</p>}
        <CDataTable
          noItemsView={{ noItems: "No hay incidencias para estos filtros.", noResults: "No hay incidencias para estos filtros." }}
          addTableClasses="table-fixed"
          fields={fields}
          items={items}
          striped
          border
          loading={loading}
          responsive
          scopedSlots={{
            issueLabel: (item: TaxpayerSifenIncident) => (
              <td className="text-center"><strong>#{item.id}</strong><div>{item.issueLabel}</div><small>{date(item.createdAt)}</small></td>
            ),
            taxPayerName: (item: TaxpayerSifenIncident) => (
              <td className="text-center"><strong>{item.taxPayerName}</strong><div>RUC: {item.taxPayerRuc}</div></td>
            ),
            cdc: (item: TaxpayerSifenIncident) => (
              <td className="text-center" style={{ minWidth: "210px", overflowWrap: "anywhere" }}><div>{item.documentNumber}</div>{item.cdc}</td>
            ),
            progressStatus: (item: TaxpayerSifenIncident) => (
              <td className="text-center">{incidentProgress[item.progressStatus]}{item.supportTicket && <div><small>Ticket: {item.supportTicket}</small></div>}</td>
            ),
            deliveryStatus: (item: TaxpayerSifenIncident) => (
              <td className="text-center">{incidentDelivery[item.deliveryStatus]}{item.sentAt && <div><small>{date(item.sentAt)}</small></div>}</td>
            ),
            actions: (item: TaxpayerSifenIncident) => (
              <td className="text-center">
                <CButtonGroup>
                  <CTooltip content="Seguimiento">
                    <CButton
                      className="text-white"
                      color="info"
                      aria-label="Seguimiento"
                      onClick={() => { setDetail(undefined); setDetailError(""); setSelectedId(item.id); setConversationId(undefined); }}
                    >
                      <i className="fa fa-eye" aria-hidden="true" />
                    </CButton>
                  </CTooltip>
                  <CTooltip content="Conversación por correo">
                    <CButton color="primary" aria-label="Conversación por correo" onClick={() => { setSelectedId(undefined); setConversationId(item.id); }}>
                      <i className="fa fa-envelope" aria-hidden="true" />
                    </CButton>
                  </CTooltip>
                </CButtonGroup>
              </td>
            ),
          }}
        />
        <div className="d-flex justify-content-center">
          <CPagination
            pages={Math.max(1, Math.ceil(count / PAGE_SIZE))}
            activePage={offset / PAGE_SIZE + 1}
            onActivePageChange={(page: number) => setOffset((page - 1) * PAGE_SIZE)}
            className={count <= PAGE_SIZE ? "d-none" : ""}
          />
        </div>
      </CCardBody>
    </CCard>
    {conversationId !== undefined && <SifenIncidentConversationModal key={conversationId} incidentId={conversationId} taxPayerId={taxPayerId}
      onClose={() => setConversationId(undefined)} onSent={() => setReload(value => value + 1)} />}
    {selectedId !== undefined && <CModal show size="xl" onClose={() => setSelectedId(undefined)}>
      <CModalHeader closeButton><CModalTitle>Seguimiento de incidencia #{selectedId}</CModalTitle></CModalHeader>
      <CModalBody>
        {detailError && <div className="alert alert-danger" role="alert">{detailError}</div>}
        {!detail && !detailError && <p role="status">Cargando seguimiento...</p>}
        {detail && <>
          <h2 className="h5">{detail.taxPayerName} · RUC {detail.taxPayerRuc}</h2>
          <p style={{ overflowWrap: "anywhere" }}><strong>Incidencia:</strong> {detail.issueLabel}<br /><strong>Documento:</strong> {detail.documentNumber}<br /><strong>CDC:</strong> {detail.cdc}</p>
          <p><strong>Seguimiento:</strong> {incidentProgress[detail.progressStatus]}<br /><strong>Correo:</strong> {incidentDelivery[detail.deliveryStatus]}<br /><strong>Ticket SIFEN:</strong> {detail.supportTicket || "Pendiente de asignación"}<br /><strong>Enviado:</strong> {date(detail.sentAt)}<br /><strong>Próximo intento:</strong> {date(detail.nextAttemptAt)}</p>
          {detail.deliveryStatus === "failed" && <p className="alert alert-info">Su incidencia continúa registrada. El administrador revisará el envío del correo.</p>}
          {detail.details && <><h3 className="h5">Observaciones reportadas</h3><p style={{ whiteSpace: "pre-wrap" }}>{detail.details}</p></>}
          <h3 className="h5">Historial de seguimiento</h3>
          <ul className="list-unstyled">{detail.events?.filter(event => event.kind !== "email_received" && !event.kind.startsWith("email_reply_")).map((event) => <li key={event.id} className="border-bottom py-2">
            <small>{date(event.createdAt)}</small><div style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{event.note}</div>
          </li>)}</ul>
        </>}
      </CModalBody>
      <CModalFooter><CButton color="primary" onClick={() => setDetailReload(value => value + 1)}>Actualizar seguimiento</CButton><CButton color="secondary" onClick={() => setSelectedId(undefined)}>Cerrar</CButton></CModalFooter>
    </CModal>}
  </>;
};

const SifenIncidentList = () => {
  const taxPayer = useSelector((state: RootState) => state.taxPayer.data.taxPayer);
  const permissions = useSelector((state: RootState) => state.taxPayerPermissions);
  if (taxPayer.id === undefined) return <p>Seleccione un contribuyente para ver sus incidencias.</p>;
  if (!permissions.loaded || permissions.taxPayerId !== taxPayer.id)
    return <p role="status">Cargando permisos...</p>;
  const allowedPermissions = SIFEN_INCIDENT_VIEW_PERMISSIONS.filter((permission) => permissions.permissions.includes(permission));
  if (!allowedPermissions.length)
    return <PermissionRequired />;
  // Discard previous rows and open details when the company or access changes.
  return <TaxpayerIncidents key={`${taxPayer.id}:${allowedPermissions.join(",")}`} taxPayerId={taxPayer.id} taxPayerName={taxPayer.socialName} />;
};

export default SifenIncidentList;
